/**
 * A one-for-one port of `apps/mobile/src/standings/standingsViewModel.ts`,
 * with logoUrl carried through for the team mark (ISSUE-1087).
 */
import {
  STANDINGS_EMPTY_BODY,
  STANDINGS_EMPTY_TITLE,
  type typeStandingsEnvelope,
  type typeStandingsGroupView,
  type typeStandingsRowDto,
  type typeStandingsTableRow,
  type typeStandingsView,
} from "./standingsTypes";

function mapRow(row: typeStandingsRowDto): typeStandingsTableRow {
  const values = Object.fromEntries(
    row.cells.map((cell) => [cell.key, cell.display]),
  );
  return {
    id: row.teamId,
    rankLabel: row.rankLabel,
    teamName: row.teamName,
    teamAbbr: row.teamAbbr,
    logoUrl: row.logoUrl ?? null,
    shortName: row.shortName ?? null,
    values,
  };
}

export function buildStandingsView(input: {
  isPending: boolean;
  isError: boolean;
  envelope: typeStandingsEnvelope | undefined;
}): typeStandingsView {
  if (input.isPending) {
    return { kind: "loading" };
  }
  if (input.isError) {
    return { kind: "error", code: "INTERNAL_ERROR" };
  }
  if (input.envelope === undefined) {
    return { kind: "error", code: "INTERNAL_ERROR" };
  }
  const error = input.envelope.error ?? null;
  const data = input.envelope.data ?? null;
  if (error !== null) {
    return { kind: "error", code: error.code };
  }
  if (data === null) {
    return { kind: "error", code: "INTERNAL_ERROR" };
  }
  const hasNoRows =
    data.groups.length === 0 ||
    data.groups.every((group) => group.rows.length === 0);
  if (hasNoRows) {
    return {
      kind: "empty",
      title: STANDINGS_EMPTY_TITLE,
      body: data.emptyReason ?? STANDINGS_EMPTY_BODY,
    };
  }

  const groupingOptions = data.groupings.length > 1 ? data.groupings : [];
  const groups: typeStandingsGroupView[] = [];
  let previousParent: string | null = null;
  for (const group of data.groups) {
    if (group.rows.length === 0) {
      continue;
    }
    const parentLabel = group.parentLabel ?? null;
    const sectionHeader =
      parentLabel !== null && parentLabel !== previousParent
        ? parentLabel
        : null;
    previousParent = parentLabel;
    groups.push({
      key: group.key,
      sectionHeader,
      title: group.label,
      rows: group.rows.map(mapRow),
    });
  }

  return {
    kind: "ready",
    leagueId: data.leagueId,
    leagueName: data.leagueName,
    seasonLabel: data.seasonLabel,
    groupingOptions,
    selectedGroupingKey: data.grouping,
    primaryColumns: data.primaryColumns,
    secondaryColumns: data.secondaryColumns,
    groups,
  };
}

/**
 * The most stat columns a standings card holds two across at desktop width
 * (SMOKE-4). Two NFL division cards at 1440 are 530px each, and the NFL's ten
 * stat columns need 598px, so HOME was cut mid-glyph and STRK hidden. A table
 * with more columns than this gets the full width, one card per row.
 */
export const STANDINGS_TWO_ACROSS_MAX_COLUMNS = 6;

/** Whether the ready view's group cards sit two across at lg and up. */
export function isStandingsTwoAcross(view: {
  primaryColumns: readonly unknown[];
  secondaryColumns: readonly unknown[];
}): boolean {
  return (
    view.primaryColumns.length + view.secondaryColumns.length <=
    STANDINGS_TWO_ACROSS_MAX_COLUMNS
  );
}
